<template>
  <div ref="root" class="smart-actions-tab-bar" role="tablist" aria-label="Smart Actions" data-testid="smart-actions-tab-bar">
    <button
      v-for="tab in tabs"
      :id="tabElementId(tab.id)"
      :key="tab.id"
      type="button"
      role="tab"
      class="smart-actions-tab-bar__tab"
      :class="{ 'smart-actions-tab-bar__tab--active': tab.id === modelValue }"
      :aria-selected="tab.id === modelValue"
      :aria-controls="`${idPrefix}-panel-${tab.id}`"
      :aria-label="accessibleName(tab)"
      :tabindex="tab.id === modelValue ? 0 : -1"
      :title="tab.fullLabel"
      :data-testid="`smart-actions-tab-${tab.id}`"
      @click="select(tab.id)"
      @keydown="onKeydown($event, tab.id)"
    >
      <span class="smart-actions-tab-bar__icon">
        <Icon :name="tab.icon" size="sm" aria-hidden="true" data-testid="smart-actions-tab-icon" />
        <span v-if="tab.badgeCount" class="smart-actions-tab-bar__badge" aria-hidden="true" data-testid="smart-actions-tab-badge">
          {{ badgeText(tab.badgeCount) }}
        </span>
      </span>
      <span class="smart-actions-tab-bar__label">{{ tab.label }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Icon } from '@/storybook';

export interface SmartActionsTab {
  id: string;
  label: string;
  fullLabel: string;
  icon: string;
  badgeCount?: number;
}

const MAX_VISIBLE_BADGE_COUNT = 99;

const props = defineProps<{
  tabs: SmartActionsTab[];
  modelValue: string;
  idPrefix: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [id: string];
}>();

const root = ref<HTMLElement | null>(null);

const tabElementId = (id: string) => `${props.idPrefix}-tab-${id}`;

const badgeText = (count: number) => (count > MAX_VISIBLE_BADGE_COUNT ? `${MAX_VISIBLE_BADGE_COUNT}+` : String(count));

// The badge is aria-hidden so the capped "99+" never reaches assistive tech;
// the real count rides in the tab's accessible name instead.
const accessibleName = (tab: SmartActionsTab) => (tab.badgeCount ? `${tab.fullLabel}, ${tab.badgeCount} open` : tab.fullLabel);

const select = (id: string) => {
  emit('update:modelValue', id);
};

const onKeydown = (event: KeyboardEvent, currentId: string) => {
  const ids = props.tabs.map(tab => tab.id);
  const index = ids.indexOf(currentId);
  const targets: Record<string, string | undefined> = {
    ArrowRight: ids[(index + 1) % ids.length],
    ArrowLeft: ids[(index - 1 + ids.length) % ids.length],
    Home: ids[0],
    End: ids[ids.length - 1],
  };
  const next = targets[event.key];
  if (!next) {
    return;
  }

  event.preventDefault();
  select(next);
  const buttons = Array.from(root.value?.querySelectorAll<HTMLElement>('[role="tab"]') ?? []);
  buttons.find(button => button.id === tabElementId(next))?.focus();
};
</script>

<style scoped>
.smart-actions-tab-bar {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  @apply border-b border-zinc-200 dark:border-zinc-700;
}

.smart-actions-tab-bar__tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  min-height: 44px;
  min-width: 0;
  padding: 10px 2px;
  margin-bottom: -1px;
  border-bottom: 3px solid transparent;
  @apply rounded-t-md text-zinc-500 dark:text-zinc-400 cursor-pointer outline-none transition-colors select-none;
  @apply hover:text-zinc-900 dark:hover:text-zinc-100;
  @apply focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400;
}

.smart-actions-tab-bar__tab--active {
  @apply text-blue-500 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-400 border-blue-500 dark:border-blue-400;
}

.smart-actions-tab-bar__icon {
  position: relative;
  display: flex;
  width: 20px;
  height: 20px;
}

.smart-actions-tab-bar__badge {
  position: absolute;
  top: -8px;
  left: 14px;
  min-width: 18px;
  padding: 1px 5px;
  border-radius: 9px;
  font-size: 10px;
  line-height: 14px;
  text-align: center;
  @apply box-border font-bold text-white bg-blue-500 dark:bg-blue-400;
}

.smart-actions-tab-bar__label {
  max-width: 100%;
  font-size: 12px;
  line-height: 16px;
  @apply font-semibold whitespace-nowrap overflow-hidden text-ellipsis;
}

.smart-actions-tab-bar__tab--active .smart-actions-tab-bar__label {
  @apply font-bold;
}
</style>
